import React, { useEffect, useState } from 'react';
import api from '../../services/api';

export default function GroupPoll() {
	const [code, setCode] = useState('');
	const [groupLabel, setGroupLabel] = useState('');
	const [session, setSession] = useState(null);
	const [activeQuestion, setActiveQuestion] = useState(null);
	const [availableGroups, setAvailableGroups] = useState([]);
	const [answer, setAnswer] = useState('');
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');

	const cardStyle = {
		maxWidth: 960,
		margin: '0 auto',
		padding: 24
	};

	const inputStyle = {
		width: '100%',
		padding: '12px 14px',
		border: '1px solid var(--border-color)',
		borderRadius: 0,
		background: 'var(--background-secondary)'
	};

	const buttonPrimary = {
		padding: '12px 16px',
		borderRadius: 0,
		border: 'none',
		background: 'var(--primary-color)',
		color: 'var(--text-inverse)',
		fontWeight: 600,
		cursor: 'pointer',
		boxShadow: '0 8px 20px rgba(216,184,137,0.35)'
	};

	const buttonSecondary = {
		padding: '10px 14px',
		borderRadius: 0,
		border: '1px solid var(--border-color)',
		background: 'var(--background-secondary)',
		color: 'var(--text-primary)',
		fontWeight: 500,
		cursor: 'pointer'
	};

	async function loadState(sessionCode) {
		setError('');
		try {
			const data = await api.request(`/polls/state/${sessionCode}`);
			setSession(data.session);
			setActiveQuestion(data.activeQuestion || null);
			setAvailableGroups(Array.isArray(data.groups) ? data.groups : []);
		} catch (e) {
			setError(e.message || 'Failed to load session');
		}
	}

	const handleJoin = async (e) => {
		e.preventDefault();
		if (!code) return;
		await loadState(code.toUpperCase());
	};

    const handleSubmit = async (e) => {
		e.preventDefault();
        if (!session || !activeQuestion) return;
        if (!groupLabel) { setError('Please select a group before submitting.'); return; }
        // Prevent submitting to a non-active question for the selected group
        if (activeQuestion.group_label && activeQuestion.group_label !== groupLabel) {
            setError('No active question for this group yet.');
            return;
        }
		if (!answer.trim()) return;
		setLoading(true);
		setError('');
		try {
			await api.request('/polls/answer', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ code: session.code, questionId: activeQuestion.id, groupLabel: groupLabel || null, answerText: answer })
			});
			setAnswer('');
		} catch (e) {
			setError(e.message || 'Failed to submit');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (!session?.code) return;
		const id = setInterval(() => loadState(session.code), 5000);
		return () => clearInterval(id);
	}, [session?.code]);

    // Keep the active question controlled by the server to ensure
    // submissions count toward the currently active question on the board.

	return (
		<div style={cardStyle}>
			<div style={{ marginBottom: 16 }}>
				<h1 style={{ fontSize: 36, lineHeight: 1.2, margin: 0 }}>Group Poll</h1>
				<p style={{ color: 'var(--text-secondary)', marginTop: 6 }}>Submit answers as a group in real time</p>
			</div>

			{!session && (
				<div style={{ background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 20 }}>
					<form onSubmit={handleJoin}>
						<div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 12 }}>
							<div>
								<label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Session Code</label>
								<input value={code} onChange={e => setCode(e.target.value.toUpperCase())} placeholder="e.g. ABC123" style={inputStyle} />
							</div>
							<div>
								<label style={{ fontSize: 12, color: 'var(--text-muted)' }}>Group</label>
								<input value={groupLabel} onChange={e => setGroupLabel(e.target.value)} placeholder="e.g. Group 1" style={inputStyle} />
							</div>
							<div style={{ display: 'flex', alignItems: 'flex-end' }}>
								<button type="submit" style={buttonPrimary}>Join</button>
							</div>
						</div>
					</form>
				</div>
			)}

			{session && (
				<div style={{ display: 'grid', gap: 16 }}>
					<div style={{ background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 16 }}>
						<div style={{ fontWeight: 600 }}>
							<span>Session:</span> <span>{session.title} ({session.code})</span> {groupLabel ? <span> · <span>Group:</span> <span>{groupLabel}</span></span> : null}
						</div>
					</div>


					<div style={{ background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 16 }}>
						<div style={{ fontWeight: 600, marginBottom: 8 }}>Choose your group</div>
						<div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 12 }}>
							<select value={groupLabel} onChange={e => setGroupLabel(e.target.value)} style={{ ...inputStyle, padding: '12px 10px' }}>
								<option value="">Select group…</option>
								{availableGroups.map(g => <option key={g} value={g}>{g}</option>)}
							</select>
							<button type="button" onClick={() => setGroupLabel('')} style={buttonSecondary}>Clear</button>
						</div>
						{availableGroups && availableGroups.length > 0 ? (
							<div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
								{availableGroups.map(g => (
									<button key={g} type="button" onClick={() => setGroupLabel(g)} style={{ padding: '8px 12px', borderRadius: 0, border: '1px solid var(--border-color)', background: groupLabel === g ? 'var(--primary-color)' : 'var(--background-secondary)', color: groupLabel === g ? 'var(--text-inverse)' : 'var(--text-primary)', cursor: 'pointer' }}>
										{g}
									</button>
								))}
							</div>
						) : null}
						{activeQuestion && groupLabel && activeQuestion.group_label && activeQuestion.group_label !== groupLabel ? (
							<div style={{ color: 'var(--text-secondary)', marginTop: 8 }}>No active question for this group yet.</div>
						) : null}
					</div>

					<div style={{ background: 'var(--background-secondary)', border: '1px solid var(--border-color)', borderRadius: 0, padding: 20 }}>
					{activeQuestion ? (
							<form onSubmit={handleSubmit}>
								<div style={{ fontSize: 24, fontWeight: 700, marginBottom: 12 }}>{activeQuestion.question_text}</div>
								<textarea value={answer} onChange={e => setAnswer(e.target.value)} rows={5} placeholder="Type your answer..." style={{ ...inputStyle, resize: 'vertical' }} />
								<div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
									<button type="submit" disabled={loading} style={{ ...buttonPrimary, opacity: loading ? 0.7 : 1 }}>{loading ? 'Submitting…' : 'Submit Answer'}</button>
									<button type="button" onClick={() => setAnswer('')} style={buttonSecondary}>Clear</button>
								</div>
							</form>
					) : (
						<div style={{ color: 'var(--text-secondary)' }}>No question configured for this group yet. Please check with your instructor.</div>
					)}
					</div>
				</div>
			)}

			{error ? <div style={{ color: 'var(--error-color)', marginTop: 12, fontWeight: 600 }}>{error}</div> : null}
		</div>
	);
}


